import {
  add,
  always,
  applySpec,
  assoc,
  clone,
  compose,
  curry,
  divide,
  find,
  flip,
  gte,
  identity,
  ifElse,
  includes,
  indexBy,
  isEmpty,
  isNil,
  map,
  mapObjIndexed,
  multiply,
  path,
  pathEq,
  prop,
  propEq,
  propOr,
  subtract,
  sum
} from "ramda";

export const divideTo = flip(divide);
export const subtractTo = flip(subtract);

export const perMonth = prop('month');
export const perDay = prop('day');

export const nameIs = propEq('name');
export const slugIs = propEq('slug');
export const nothing = always(null);
export const employersFrom = propOr(1, 'employers');
export const energyFrom = propOr(1, 'energy');
export const energyForSqFrom = propOr(1, 'energy_for_cube');
export const areaFrom = propOr(1, 'area');
export const costInOneSq = propOr(NaN, 'oneSq');


/**
 * Формирует объект опции выпадающего списка
 * @param {object} any
 * @returns {object}
 */
export const optionSpec = applySpec({
  text: prop('label'),
  value: prop('name')
});

/**
 * Функция принимает сформированный объект производительности
 * и возвращающая функцию вычисляющюю результат
 * @param {object} capability - {day, week, month}
 * @return {Function} applySpec
 */
export const resultSpecFrom = (capability) => {
  let {day, week, month} = capability;
  /**
   * Функция принимает одно числовое значение и возвращает
   * вычисленный объект результатов
   * @param {Number} value -
   * @return {object} applySpec - {oneSq, day, week, month}
   */
  return applySpec({
    oneSq: multiply(1),
    day: multiply(day),
    week: multiply(week),
    month: multiply(month)
  })
};


/**
 * Функция принимает одно числовое значение производительности
 * и возвращает вычисленный объект производительности
 * по интервалам день, неделя, месяц
 * @param {Number} capability - производительность
 * @return {object} - {day, week, month}
 */
export const capabilitySpec = applySpec({
  day: multiply(1),
  week: multiply(7),
  month: multiply(30)
});

/**
 * Формирует объект для отобрадения в таблице расходов
 * @param {Array} ingredients
 * @returns {object}
 */
export const resultPageSpec = applySpec({
  ingredients: identity,
  sum: compose(
    sum,
    map(prop('price'))
  )
});

/**
 * Приводит объект ингридиента в нормальный вид
 * @param {object} ingredient
 * @returns {[]}
 */
export const normalize = map(({value, min, max, step, ingredient}) => {
  return {
    ...ingredient,
    value: {
      min: min,
      max: max,
      step: step,
      value: value
    }
  }
});

/**
 * Функция расчета окупаемости
 * Возвращает масссив из сумм
 * @param price
 * @param profit
 * @returns {[]}
 */
export const calcPayback = (profit, price = 0) => {
  let res = [];
  const calc = (profit, sum = 0) => {
    let _sum = add(sum, profit);
    if (gte(Math.abs(_sum), price)) return _sum;
    res.push(_sum.toFixed(2));
    return calc(profit, _sum)
  };
  try {
    calc(profit);
  } catch (e) {
    console.error(e);
    return res;
  }
  return res;
};

export const defaultOr = (def, val) => ifElse(isNil, always(def), identity)(val);

export const getValue = curry((name, source) => path([name, 'value'], source));

export const convertToCurrency = (currencyRate = 0) => mapObjIndexed(
  ifElse(
    (v, key) => includes(key, ['min', 'max', 'step', 'value']),
    (val, key) => {
      let result = divideTo(currencyRate, val);
      switch (key) {
        case 'min':
        case 'max':
        case 'step':
          result = result.toFixed(6);
          break;
      }
      return parseFloat(result);
    },
    identity
  )
);

export const normalizeIngredients = (ingredients, currency, recipe, line) => map((ing) => {
  const amount = (ing) => {
    if (ing.name === 'rent') return {
      value: line.area,
      max: line.area * 3,
      min: line.area,
      step: 1
    };
    if (ing.name === 'energyTariff') return {
      value: line.energy_for_cube,
      max: line.energy_for_cube,
      min: line.energy_for_cube,
      step: 0,
      editable: false
    };
    return clone(path([ing.name, 'value'], recipe));
  };


  const price = (ing) => ifElse(
    isNil,
    compose(
      convertToCurrency(currency.rate),
      always({
        min: ing.min_price,
        max: ing.max_price,
        step: ing.step,
        value: ing.price
      })
    ),
    identity
  )(find(pathEq(['currency', 'code'], currency.code), ing.prices));

  return {
    name: ing.name,
    label: ing.label,
    measure: ing.measure,
    purchase: {
      measure: ing.purchase_measure || '',
      multiplier: ing.measure_multiplier || 1
    },
    amount: amount(ing),
    price: price(ing)
  };
})(ingredients);

export const normalizeRecipe = ifElse(
  isEmpty, nothing,
  compose(
    (recipe) => assoc('index', indexBy(prop('name'), recipe.ingredients), recipe),
    (recipe) => assoc('ingredients', normalize(recipe.ingredients), recipe),
    clone
  )
);
